import React, { useState, useEffect } from 'react';
import { Home, LayoutGrid, Store, User, ShoppingCart, Flame } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useUser } from '../contexts/UserContext';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { ProfileModal } from './ProfileModal';

interface MobileBottomNavProps {
    isDark: boolean;
    onLogout: () => void;
}

export const MobileBottomNav: React.FC<MobileBottomNavProps> = ({ isDark, onLogout }) => {
    const { profile, user } = useUser();
    const navigate = useNavigate();
    const location = useLocation();
    const [showProfileModal, setShowProfileModal] = useState(false);
    const [activeTab, setActiveTab] = useState('home');

    const cartItemsCount = profile?.cart?.reduce((acc, item) => acc + item.quantity, 0) || 0;
    const userAvatar = profile?.photoURL || user?.photoURL || "https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=300";
    const userName = profile?.name || user?.displayName || "مستخدم جديد";

    // Don't render on admin, login, checkout routes
    const isHiddenRoute =
        location.pathname.startsWith('/admin') ||
        location.pathname === '/checkout' ||
        location.pathname === '/login';

    // Deals hype logic: Active phase vs Waiting phase
    const [dealsState, setDealsState] = useState<'waiting' | 'active' | 'ended' | 'none'>('none');

    useEffect(() => {
        const unsub = onSnapshot(doc(db, 'pageConfigs', 'today_deals'), (docSnap) => {
            if (docSnap.exists()) {
                const config = docSnap.data();
                if (!config.hasTimer) {
                    setDealsState('none');
                    return;
                }

                const checkState = () => {
                    const now = +new Date();
                    const startDate = config.timerStartDate ? +new Date(config.timerStartDate) : null;
                    const endDate = config.timerEndDate ? +new Date(config.timerEndDate) : null;

                    if (startDate && startDate > now) {
                        setDealsState('waiting');
                    } else if (endDate && endDate > now) {
                        setDealsState('active');
                    } else if (startDate || endDate) {
                        setDealsState('ended');
                    } else {
                        setDealsState('none');
                    }
                };
                
                checkState();
                const interval = setInterval(checkState, 1000);
                return () => clearInterval(interval);

            } else {
                setDealsState('none');
            }
        });
        return unsub;
    }, []);

    useEffect(() => {
        const path = location.pathname;
        if (path === '/') {
            const savedView = localStorage.getItem('currentPage');
            if (savedView === 'cart') {
                setActiveTab('cart');
            } else if (savedView === 'category') {
                setActiveTab('categories');
            } else {
                setActiveTab('home');
            }
        } else if (path.startsWith('/category/')) {
            setActiveTab('categories');
        } else if (path === '/shops') {
            setActiveTab('shops');
        } else if (path === '/page/today_deals') {
            setActiveTab('today_deals');
        } else if (path === '/search') {
            setActiveTab('home'); 
        } else if (path === '/account') {
            setActiveTab('account');
        } else if (path === '/cart') {
            setActiveTab('cart');
        } else {
            setActiveTab('');
        }
    }, [location.pathname, location.search]);

    const handleNavigation = (tab: string) => {
        // Add haptic feedback if available
        if (typeof window !== 'undefined' && window.navigator && window.navigator.vibrate) {
            window.navigator.vibrate(50);
        }

        const isCurrentlyActive = activeTab === tab;
        setActiveTab(tab);

        switch (tab) {
            case 'home':
                localStorage.setItem('currentPage', 'home');
                if (isCurrentlyActive && location.pathname === '/') {
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                } else {
                    navigate('/', { state: { view: 'home', clearModals: true }, replace: true });
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                }
                break;
            case 'categories':
                if (location.pathname === '/' || location.pathname.startsWith('/category/')) {
                    navigate('/', { state: { view: 'home' } });
                    setTimeout(() => window.scrollTo({ top: 500, behavior: 'smooth' }), 100);
                } else {
                    navigate('/', { state: { view: 'home' } });
                    setTimeout(() => window.scrollTo({ top: 500, behavior: 'smooth' }), 100);
                }
                break;
            case 'today_deals':
                if (isCurrentlyActive && location.pathname === '/page/today_deals') {
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                } else {
                    navigate('/page/today_deals');
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                }
                break;
            case 'account':
                if (isCurrentlyActive && location.pathname === '/account') {
                    // Force re-render of account page to main view by pushing state
                    navigate('/account', { replace: true, state: { resetView: true, timestamp: Date.now() } });
                } else {
                    navigate('/account');
                }
                break;
            case 'cart':
                if (isCurrentlyActive && location.pathname === '/cart') {
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                } else {
                    navigate('/cart');
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                }
                break;
        }
    };

    if (isHiddenRoute) return null;

    const navItems = [
        { id: 'home', icon: Home, label: 'الرئيسية' },
        { id: 'categories', icon: LayoutGrid, label: 'الأقسام' },
        { id: 'today_deals', icon: Flame, label: 'عروض اليوم' },
        { id: 'account', icon: User, label: 'حسابي' },
        { id: 'cart', icon: ShoppingCart, label: 'العربة' },
    ];

    const activeIndex = navItems.findIndex(item => item.id === activeTab);

    // Liquid Glass Style
    const glassStyle = isDark
        ? 'bg-[#0f1115]/85 border-t border-white/10 shadow-[0_-4px_16px_rgba(0,0,0,0.4)] backdrop-blur-md'
        : 'bg-white/80 border-t border-white/60 shadow-[0_-4px_16px_rgba(59,130,246,0.1)] backdrop-blur-md';

    return (
        <>
            <div className={`md:hidden fixed bottom-0 left-0 right-0 z-[5000] px-2 pb-[env(safe-area-inset-bottom)] pt-1.5 transition-all duration-500 rounded-t-[1.5rem] overflow-hidden ${glassStyle}`} style={{ direction: 'rtl' }}>
                


                <div className="flex justify-around items-center h-[64px] relative z-10">
                    {/* Sliding Indicator - Fully occupying the button space to prevent text cutoff */}
                    {activeIndex !== -1 && (
                        <div 
                            className={`absolute top-0 bottom-0 w-[calc(100%/5)] rounded-[1.5rem] transition-all duration-200 ease-[cubic-bezier(0.34,1.56,0.64,1)] z-0 ${isDark ? 'bg-blue-500/20 shadow-[inset_0_0_12px_rgba(59,130,246,0.3)]' : 'bg-blue-500/10 shadow-[inset_0_0_12px_rgba(255,255,255,0.8)] border border-white/50'}`}
                            style={{ 
                                right: `calc(${activeIndex * 20}%)`,
                            }}
                        />
                    )}

                    {navItems.map((item, index) => {
                        const Icon = item.icon;
                        const isActive = activeTab === item.id;
                        // Hype deal is only active if Deals are actively running
                        const isHypedDeal = item.id === 'today_deals' && dealsState === 'active';

                        return (
                            <button
                                key={item.id}
                                onClick={() => handleNavigation(item.id)}
                                className={`relative flex flex-col items-center justify-center w-full h-full z-10 transition-all duration-150 group`}
                            >
                                <div className={`relative flex items-center justify-center transition-all duration-200 ease-out ${isActive ? 'translate-y-[-2px] scale-110' : 'translate-y-1 scale-90 opacity-60 group-hover:opacity-100 group-hover:scale-100 group-active:scale-95'}`}>
                                    {isHypedDeal ? (
                                        <div className="relative">
                                           <Icon
                                                className={`w-[24px] h-[24px] transition-colors duration-300 text-orange-500 drop-shadow-[0_0_8px_rgba(249,115,22,0.8)] animate-pulse`}
                                                strokeWidth={2.5}
                                                fill="currentColor"
                                           />
                                           <span className="absolute -top-3 -right-6 md:-right-8 bg-red-600/90 backdrop-blur-sm text-white text-[8px] md:text-[9px] px-1.5 py-0.5 rounded-full font-black animate-bounce whitespace-nowrap border border-white/20 shadow-lg" style={{ direction: 'rtl' }}>الحق الآن 🔥</span>
                                        </div>
                                    ) : (
                                        <Icon
                                            className={`w-[22px] h-[22px] transition-colors duration-300 ${isActive ? (isDark ? 'text-blue-400 drop-shadow-[0_0_8px_rgba(96,165,250,0.4)]' : 'text-blue-600') : (isDark ? 'text-slate-300' : 'text-slate-600')}`}
                                            strokeWidth={isActive ? 2.5 : 2}
                                        />
                                    )}
                                    
                                    {item.id === 'cart' && cartItemsCount > 0 && (
                                        <div className={`absolute -top-1.5 -right-2 w-[18px] h-[18px] rounded-full flex items-center justify-center text-[10px] font-black text-white border-[1.5px] ${isDark ? 'bg-rose-500 border-[#121418]' : 'bg-rose-500 border-white'} shadow-sm animate-bounce-soft`}>
                                            {cartItemsCount}
                                        </div>
                                    )}
                                </div>
                                <span className={`text-[9px] font-black transition-all duration-150 absolute bottom-1 mt-1 ${isActive ? 'opacity-100 translate-y-0 ' + (isDark ? 'text-blue-400' : 'text-blue-600') : 'opacity-0 translate-y-2 ' + (isDark ? 'text-slate-300' : 'text-slate-500')}`}>
                                    {item.label}
                                </span>
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Removed ProfileModal from here; handled exclusively by Header now */}
        </>
    );
};
